import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Region, SeatsioSeatingChart } from '@seatsio/seatsio-react';
import type { EventManager, SeatingChart } from '@seatsio/seatsio-types';

import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import type { SeatsSelectionChartProps } from 'features/common/hooks/useSeatsSelection';

import styles from './PunchupBuyerSeatsWidget.module.css';
import { SEATS_COLORS, SEATS_STYLE, toSeatsLanguage } from '../PunchupSeatsWidget/seatsTheme';

interface PunchupBuyerSeatsWidgetProps {
	eventKey: string | null | undefined;
	chartProps?: SeatsSelectionChartProps;
	/**
	 * seats.io session mode. Defaults to `undefined` (no server-side hold — local selection only).
	 * Pass `'start'` to begin a new session and place a hold for each selected seat (e.g. the
	 * admin comp-tickets flow, where seats must be reserved while the operator fills out the
	 * recipient form). `'continue'` resumes the buyer's existing session token.
	 */
	session?: 'start' | 'continue' | 'manual';
}

export const PunchupBuyerSeatsWidget: FC<PunchupBuyerSeatsWidgetProps> = ({ eventKey, chartProps, session }) => {
	const { i18n } = useTranslation();
	const { partnerData } = useGetPartnerData();

	if (!eventKey || !partnerData?.seats_io_workspace_key) {
		return null;
	}

	const region = (partnerData.seats_region || 'na') as Region;

	// SeatingChart and EventManager both expose deselectObjects — the only method the hook uses.
	const { onChartRendered: hookOnChartRendered, ...restChartProps } = chartProps ?? {};
	const bridgedOnChartRendered = hookOnChartRendered
		? (chart: SeatingChart) => hookOnChartRendered(chart as unknown as EventManager)
		: undefined;

	return (
		<div className={styles.container}>
			<SeatsioSeatingChart
				workspaceKey={partnerData.seats_io_workspace_key}
				event={eventKey}
				region={region}
				colorScheme="light"
				language={toSeatsLanguage(i18n.language)}
				colors={SEATS_COLORS}
				style={SEATS_STYLE}
				{...(session ? { session } : undefined)}
				{...restChartProps}
				{...(bridgedOnChartRendered ? { onChartRendered: bridgedOnChartRendered } : undefined)}
			/>
		</div>
	);
};
